import { GearItem } from "@highmarch/domain";
import { feedCandidates, itemCard } from "@highmarch/app";
import { RARITY_LABEL, pctStep } from "../gearUi";
import { tickNumber } from "../kit";
import { toast } from "../toast";
import type { ArmouryCtx } from "./host";
import { btn, essImg, essenceEl, h, ico, newSince, primaryAfterPlus, refusal, sigilImg, statShort } from "./itemBits";
import { raiseItemOn } from "./itemActions";
import { levelUp } from "./itemFx";
import { fusionLine, raiseBox } from "./forgeModel";

/**
 * The item card's Forge section (gear v2, docs/GEAR_V2.md): the item level against its cap (and who sets it — upgrade
 * that tower to go higher), what the next level gives, Raise and Raise max for Forge Essence with the level-up
 * flourish; then fusion readiness (FUSE) and Feed spares.
 */

export interface ForgeHooks {
  /** Re-render the card after a change made here (the page re-rendered already through ctx.changed). */
  readonly after: () => void;
  /** The card's root, for the flourish on its disc after the re-render. */
  readonly root: HTMLElement;
  readonly openFusion: (keeper: GearItem) => void;
  readonly openFeed: (target: GearItem) => void;
}

/** A level and its cap as the card's bar shows it. */
const TENTHS = 1000;

export function forgeSection(ctx: ArmouryCtx, it: GearItem, hooks: ForgeHooks): HTMLElement {
  const profile = ctx.profile, B = ctx.B;
  const view = itemCard(profile, it);
  const box = raiseBox(profile, view);
  const on = it.equippedTo;
  const sec = h("div", `aforge${on !== null ? " primary" : ""}${box.atCap ? " capped" : ""}`);

  // Level against the cap: "Lv 34 / 40", a bar, the essence held.
  const now = h("i", "now");
  now.style.transform = `scaleX(${box.fill})`;
  const bar = h("span", "lbar");
  if (box.max !== null) {
    // Raise max's reach, striped green behind the gold: how far the essence held would take it.
    const ghost = h("i", "max");
    ghost.style.transform = `scaleX(${box.cap <= 1 ? 1 : (box.max.to - 1) / (box.cap - 1)})`;
    bar.append(ghost);
  }
  bar.append(now);
  const held = h("span", "held", essImg(), String(box.essence));
  held.setAttribute("aria-label", `${box.essence} Forge Essence held`);
  sec.append(h("div", "fhd", h("span", "lv disp", h("small", "", "ITEM LEVEL "), h("b", "lvn", String(box.level)), h("small", "cap", ` / ${box.cap}`)), bar, held));
  const capRow = h("div", `capl${box.atCap ? " at" : ""}`, ico(box.atCap ? "lock" : "tower", 12), h("span", "", box.capLine));
  if (box.atCap) {
    const go = btn("abtn blue sm towersgo", h("span", "disp", "Towers"), ico("chev", 12));
    go.addEventListener("click", () => ctx.openTowers());
    capRow.append(go);
  }
  sec.append(capRow);

  if (box.next !== null) {
    const { now, next } = primaryAfterPlus(B, it, on);
    const gain = box.next.powerNext - box.next.powerNow, [nowPct, nextPct] = pctStep(now, next);
    const nextLine = h("div", "fnext", h("b", "", `Lv ${box.next.level}`), ` ${nowPct} → `, h("em", "", nextPct), ` ${statShort(it.primaryStat)}${gain > 0 ? ` · ⚡+${gain}` : ""}`);
    sec.append(nextLine);
    if (box.bindsOnNext) sec.append(h("small", "bind", "Raising it again binds it to your fortress"));
    else if (box.maxBinds) sec.append(h("small", "bind", `Max raises it more than ${B.gear.bindAboveRaised} levels: that binds it to your fortress`));

    const one = btn(`abtn ${on !== null ? "gold" : "blue"} raise1${box.short > 0 ? " short" : ""}`, h("span", "disp", "Raise"), essenceEl(box.next.cost, box.essence));
    one.setAttribute("aria-label", `Raise to Lv ${box.next.level} for ${box.next.cost} essence`);
    one.addEventListener("click", () => raise(false));
    const acts = h("div", "facts", one);
    if (box.max !== null) {
      const mx = btn("abtn parch raisemax", h("span", "disp", `Max → Lv ${box.max.to}`), essenceEl(box.max.cost, box.essence));
      mx.setAttribute("aria-label", `Raise to Lv ${box.max.to} for ${box.max.cost} essence`);
      mx.addEventListener("click", () => raise(true));
      acts.append(mx);
    }
    sec.append(acts);
    if (box.short > 0) sec.append(h("small", "shortw", `Need ${box.short} more essence`));
    else if (box.max === null && box.toCap > box.next.cost) sec.append(h("small", "tocap", `${box.toCap} essence to reach Lv ${box.cap}`));
  }

  function raise(max: boolean): void {
    const from = profile.findGear(it.id)?.level ?? it.level;
    const before = primaryAfterPlus(B, it, on).now;
    const r = raiseItemOn(ctx, it, max);
    if (typeof r === "string") {
      toast(ctx.toastParent, r === "not enough essence" ? `Raise to Lv ${from + 1}: need ${box.short} more essence` : refusal(r));
      return;
    }
    hooks.after();
    const disc = hooks.root.querySelector<HTMLElement>(".adisc");
    if (disc !== null) levelUp(disc, r.levels > 1 ? `LV ${from} → ${from + r.levels}` : `LV ${from + r.levels}`, it.rarity);
    const v = hooks.root.querySelector(".prim .v");
    const nowIt = profile.findGear(it.id);
    if (v !== null && nowIt !== null) tickNumber(v, Math.round(before.toNumber() * TENTHS), Math.round(primaryAfterPlus(B, nowIt, on).now.toNumber() * TENTHS), 450 + 60 * Math.min(10, r.levels), fmtTenths);
    const lv = hooks.root.querySelector(".aforge .lvn");
    if (lv !== null) tickNumber(lv, from, from + r.levels, 300 + 40 * Math.min(10, r.levels));
  }

  return h("div", "dforge", sec, fusionRow(ctx, it, hooks), feedRow(ctx, it, hooks));
}

const fmtTenths = (t: number): string => (t >= 100 ? `+${Math.round(t / 10)}%` : `+${(t / 10).toFixed(1)}%`);

/** Fusion readiness: "3 Rare → 1 Epic · ready with 2 spares" and FUSE, or what it still needs. */
function fusionRow(ctx: ArmouryCtx, it: GearItem, hooks: ForgeHooks): HTMLElement | null {
  const view = itemCard(ctx.profile, it);
  const line = fusionLine(ctx.profile, view);
  if (line.state === "top") return null;
  const art = h("span", "fart", sigilImg(it.rarity, "s"), ico("chev", 10), h("i", `rdot r${line.result}`));
  const row = h("div", `afuserow ${line.state}`, art, h("div", "mid", h("b", "", line.state === "ready" ? `Fuse into ${RARITY_LABEL[line.result!]}` : "Fusion"), h("small", "", line.text)));
  const go = btn(`abtn ${line.state === "ready" ? "gold" : "parch"} sm fusego`, h("span", "disp", line.state === "ready" ? "FUSE" : "Fusion"));
  go.addEventListener("click", () => hooks.openFusion(it));
  row.append(go);
  return row;
}

/** Feed spares: how many spares could go in and how high they would raise it; opens the Feed panel. */
function feedRow(ctx: ArmouryCtx, it: GearItem, hooks: ForgeHooks): HTMLElement | null {
  const view = itemCard(ctx.profile, it);
  if (view.level >= view.cap) return null;
  const cands = feedCandidates(ctx.profile, it, { roster: ctx.scope.roster, seenUpTo: newSince(ctx) });
  if (cands.length === 0) return null;
  const go = btn("abtn parch sm feedgo", h("span", "disp", "Feed"));
  go.addEventListener("click", () => hooks.openFeed(it));
  return h("div", "afeedrow", ico("bolt", 12), h("div", "mid", h("b", "", "Feed spares"), h("small", "", `${cands.length} spare item${cands.length === 1 ? "" : "s"} can melt into essence for it`)), go);
}

